import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import AmplifyAdmin from "@/components/AmplifyAdmin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, DollarSign, Activity, ShoppingBag, Loader2, Pause, Play, LifeBuoy, Send, Bot, X, Bookmark, Gift, ChevronDown, Star } from "lucide-react";

const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(1) + "K" : String(n ?? 0));
const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ icon: Icon, label, value, i }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
    className="rounded-xl border border-border/50 bg-card p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-4 h-4" /></div>
    </div>
    <div className="font-heading text-3xl font-black">{value}</div>
  </motion.div>
);

const statusBadge = (s) => {
  const map = {
    active: "bg-primary/15 text-primary border-primary/30",
    paused: "bg-chart-4/15 text-chart-4 border-chart-4/30",
    inactive: "bg-white/5 text-muted-foreground border-white/10",
    paid: "bg-primary/15 text-primary border-primary/30",
    pending: "bg-chart-4/15 text-chart-4 border-chart-4/30",
    failed: "bg-secondary/15 text-secondary border-secondary/30",
  };
  return <Badge className={`border ${map[s] || map.inactive} capitalize`}>{s || "—"}</Badge>;
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [audit, setAudit] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [canned, setCanned] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, c, o, t] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/clients"), api.get("/admin/orders"), api.get("/support/tickets"),
      ]);
      setStats(s.data); setClients(c.data.clients); setOrders(o.data.orders); setTickets(t.data.tickets);
      api.get("/support/canned").then((r) => setCanned(r.data.canned)).catch(() => {});
      api.get("/admin/comp-audit").then((r) => setAudit(r.data.entries)).catch(() => {});
      api.get("/admin/support-stats").then((r) => setSupportStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    try { const { data } = await api.get(`/support/tickets/${id}`); setActiveTicket(data); }
    catch { toast.error("Could not open ticket"); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeTicket) return;
    setReplying(true);
    try {
      const { data } = await api.post(`/support/tickets/${activeTicket.id}/messages`, { body: reply });
      setActiveTicket(data); setReply(""); toast.success("Reply sent & emailed to the customer");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); }
    finally { setReplying(false); }
  };

  const closeTicket = async (id) => {
    try { await api.post(`/support/tickets/${id}/close`); toast.success("Ticket closed"); setActiveTicket(null); load(); }
    catch { toast.error("Could not close"); }
  };

  const saveCanned = async () => {
    if (!reply.trim()) { toast.error("Write a reply first, then save it as a template"); return; }
    const title = window.prompt("Template name:");
    if (!title) return;
    try { const { data } = await api.post("/support/canned", { title, body: reply }); setCanned((c) => [...c, data]); toast.success("Template saved"); }
    catch { toast.error("Could not save template"); }
  };

  const deleteCanned = async (id) => {
    try { await api.delete(`/support/canned/${id}`); setCanned((c) => c.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete"); }
  };

  const toggleCampaign = async (client) => {
    const action = client.subscription_status === "active" ? "pause" : "resume";
    try {
      await api.post("/admin/campaign", { user_id: client.user_id, action });
      toast.success(`Campaign ${action === "pause" ? "paused" : "resumed"} for ${client.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  const comp = async (client, action, plan) => {
    let note;
    if (action === "grant") {
      note = window.prompt(`Grant free ${plan} to ${client.email}?\nOptional note (e.g. "my own channel", "influencer"):`, "");
      if (note === null) return; // cancelled
    }
    try {
      await api.post("/admin/comp", { user_id: client.user_id, action, plan, note: note || undefined });
      toast.success(action === "grant" ? `Free ${plan} access granted to ${client.email} 🎁` : `Free access revoked for ${client.email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };


  if (loading) return <DashboardShell><div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></DashboardShell>;

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-[1400px] mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-black tracking-tight">Admin Console</h1>
          <p className="text-muted-foreground mt-2">Manage clients, campaigns and revenue.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard i={0} icon={Users} label="Total clients" value={stats?.total_users ?? 0} />
          <StatCard i={1} icon={Activity} label="Active campaigns" value={stats?.active_subscriptions ?? 0} />
          <StatCard i={2} icon={DollarSign} label="Revenue" value={money(stats?.total_revenue)} />
          <StatCard i={3} icon={ShoppingBag} label="Paid orders" value={stats?.total_orders ?? 0} />
        </div>

        {supportStats && (
          <div className="rounded-xl border border-border/50 bg-gradient-to-br from-chart-4/[0.06] to-transparent p-5 mb-8" data-testid="csat-dashboard">
            <div className="flex items-center gap-2 mb-4 text-sm font-semibold"><Star className="w-4 h-4 text-chart-4" /> Support satisfaction (CSAT)</div>
            <div className="flex flex-wrap items-center gap-8">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-black" data-testid="csat-avg">{supportStats.avg_rating ?? "—"}</span>
                  <span className="text-muted-foreground text-sm">/ 5</span>
                </div>
                <div className="flex items-center gap-0.5 mt-1">
                  {supportStats.avg_rating != null
                    ? [1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= Math.round(supportStats.avg_rating) ? "text-chart-4 fill-chart-4" : "text-muted-foreground"}`} />)
                    : <span className="text-xs text-muted-foreground">No ratings yet</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{supportStats.ratings_count} rating{supportStats.ratings_count === 1 ? "" : "s"} · {supportStats.open_tickets} open</div>
              </div>
              <div className="flex-1 min-w-[240px] space-y-1.5">
                {supportStats.recent.length === 0 && <div className="text-sm text-muted-foreground">No ratings yet.</div>}
                {supportStats.recent.slice(0, 4).map((r, i) => (
                  <div key={i} className="text-xs flex items-center gap-2" data-testid={`csat-recent-${i}`}>
                    <span className="text-chart-4 font-bold shrink-0">{r.rating}★</span>
                    <span className="text-foreground truncate max-w-[140px]">{r.email}</span>
                    {r.comment && <span className="text-muted-foreground italic truncate">"{r.comment}"</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <AmplifyAdmin />

        <Tabs defaultValue="clients">
          <TabsList className="bg-card border border-border/50">
            <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">
              Support{tickets.filter((t) => t.status === "open").length > 0 && <Badge className="ml-2 bg-secondary/20 text-secondary border-secondary/30 px-1.5">{tickets.filter((t) => t.status === "open").length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clients" className="mt-6">
            {audit.length > 0 && (
              <div className="rounded-xl border border-border/50 bg-card p-4 mb-4" data-testid="comp-audit-log">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Gift className="w-4 h-4 text-primary" /> Free-access log</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {audit.map((a) => (
                    <div key={a.id} className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid={`audit-${a.id}`}>
                      <Badge className={`border px-1.5 ${a.action === "grant" ? "bg-primary/15 text-primary border-primary/30" : "bg-white/5 text-muted-foreground border-white/10"}`}>{a.action === "grant" ? `Granted ${a.plan}` : "Revoked"}</Badge>
                      <span className="text-foreground">{a.target_email}</span>
                      <span>by {a.admin_email}</span>
                      <span>· {a.at ? new Date(a.at).toLocaleString() : ""}</span>
                      {a.note && <span className="italic text-foreground/70">— "{a.note}"</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Client</TableHead><TableHead>Handle</TableHead><TableHead>Niche</TableHead>
                    <TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Followers</TableHead>
                    <TableHead className="text-right">Gained</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No clients yet</TableCell></TableRow>}
                  {clients.map((c) => (
                    <TableRow key={c.user_id} className="border-border/50" data-testid={`client-row-${c.user_id}`}>
                      <TableCell>
                        <div className="font-medium">{c.name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.tiktok_handle ? "@" + c.tiktok_handle : "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{c.target_audience?.niche || "—"}</TableCell>
                      <TableCell className="capitalize">
                        {c.plan || "—"}{c.comped && <Badge className="ml-2 bg-primary/15 text-primary border-primary/30 text-[10px]">Comped</Badge>}
                      </TableCell>
                      <TableCell>{statusBadge(c.subscription_status)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(c.current_followers)}</TableCell>
                      <TableCell className="text-right font-mono text-primary">+{fmt(c.followers_gained)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          {c.comped ? (
                            <Button size="sm" variant="outline" onClick={() => comp(c, "revoke")} className="rounded-full border-white/15 h-8" data-testid={`revoke-comp-${c.user_id}`}>
                              <X className="w-3.5 h-3.5 mr-1" /> Revoke
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" className="rounded-full border-primary/30 text-primary h-8" data-testid={`grant-comp-${c.user_id}`}>
                                  <Gift className="w-3.5 h-3.5 mr-1" /> Grant free <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {["starter", "growth", "pro"].map((p) => (
                                  <DropdownMenuItem key={p} onClick={() => comp(c, "grant", p)} className="capitalize" data-testid={`grant-${p}-${c.user_id}`}>{p} (free)</DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {c.plan && !c.comped && (
                            <Button size="sm" variant="outline" onClick={() => toggleCampaign(c)} className="rounded-full border-white/15 h-8" data-testid={`toggle-campaign-${c.user_id}`}>
                              {c.subscription_status === "active" ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</> : <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Client</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead><TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No orders yet</TableCell></TableRow>}
                  {orders.map((o) => (
                    <TableRow key={o.session_id} className="border-border/50" data-testid={`order-row-${o.session_id}`}>
                      <TableCell className="text-muted-foreground">{o.email}</TableCell>
                      <TableCell className="capitalize">{o.plan_id || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{money(o.amount)}</TableCell>
                      <TableCell>{statusBadge(o.payment_status)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Customer</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead>
                    <TableHead>Last activity</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No support messages yet</TableCell></TableRow>}
                  {tickets.map((t) => {
                    const last = t.messages?.[t.messages.length - 1];
                    return (
                      <TableRow key={t.id} className="border-border/50" data-testid={`ticket-row-${t.id}`}>
                        <TableCell><div className="font-medium">{t.name || "—"}</div><div className="text-xs text-muted-foreground">{t.email}</div></TableCell>
                        <TableCell>
                          <div className="max-w-[220px] truncate">{t.subject}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">{last?.role === "ai" && <Bot className="w-3 h-3" />}{last?.role === "customer" ? "awaiting reply" : last?.role === "ai" ? "AI replied" : "you replied"}</div>
                        </TableCell>
                        <TableCell>{statusBadge(t.status)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{t.updated_at ? new Date(t.updated_at).toLocaleString() : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openTicket(t.id)} className="rounded-full border-white/15 h-8" data-testid={`ticket-open-${t.id}`}><LifeBuoy className="w-3.5 h-3.5 mr-1" /> View</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!activeTicket} onOpenChange={(o) => !o && setActiveTicket(null)}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{activeTicket?.subject}</DialogTitle></DialogHeader>
            {activeTicket && (
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">{activeTicket.name} · {activeTicket.email} · {statusBadge(activeTicket.status)}</div>
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1" data-testid="ticket-thread">
                  {activeTicket.messages?.map((m, i) => (
                    <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m.role === "customer" ? "bg-white/5 border border-white/10" : m.role === "ai" ? "bg-primary/10 border border-primary/20" : "bg-chart-4/10 border border-chart-4/20"}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                        {m.role === "ai" && <Bot className="w-3 h-3" />}{m.role === "customer" ? "Customer" : m.role === "ai" ? "AI auto-reply" : "You"}
                      </div>
                      <div className="whitespace-pre-line">{m.body}</div>
                      {m.attachment_path && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${m.attachment_path}`} target="_blank" rel="noreferrer">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/support/files/${m.attachment_path}`} alt={m.attachment_name || "attachment"} className="mt-2 max-h-40 rounded-lg border border-white/10" data-testid="admin-message-attachment" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                {activeTicket.rating && (
                  <div className="text-sm text-muted-foreground flex items-center gap-1" data-testid="admin-ticket-rating">
                    Customer rating: <span className="text-chart-4 font-semibold">{activeTicket.rating}/5</span>
                    {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= activeTicket.rating ? "text-chart-4 fill-chart-4" : "text-muted-foreground"}`} />)}
                    {activeTicket.rating_comment && <span className="italic">— "{activeTicket.rating_comment}"</span>}
                  </div>
                )}
                {activeTicket.status !== "closed" && (
                  <div className="space-y-2">
                    {canned.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5" data-testid="canned-answers">
                        <span className="text-xs text-muted-foreground mr-1">Templates:</span>
                        {canned.map((c) => (
                          <button key={c.id} onClick={() => setReply((r) => (r ? r + "\n\n" : "") + c.body)}
                            title={c.body} className="group text-xs rounded-full border border-white/15 px-2.5 py-1 hover:border-primary/40 hover:text-primary flex items-center gap-1" data-testid={`canned-${c.id}`}>
                            {c.title}
                            <X className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:!opacity-100" onClick={(e) => { e.stopPropagation(); deleteCanned(c.id); }} />
                          </button>
                        ))}
                      </div>
                    )}
                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply — it will be emailed to the customer" className="bg-transparent border-white/10 min-h-[80px]" data-testid="ticket-reply-input" />
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={sendReply} disabled={replying} className="rounded-full bg-primary text-background font-bold" data-testid="ticket-reply-send">
                        {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Reply</>}
                      </Button>
                      <Button variant="outline" onClick={saveCanned} className="rounded-full border-white/15" data-testid="save-canned-btn"><Bookmark className="w-3.5 h-3.5 mr-1" /> Save as template</Button>
                      <Button variant="outline" onClick={() => closeTicket(activeTicket.id)} className="rounded-full border-white/15" data-testid="ticket-close-btn">Close ticket</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
};

export default AdminDashboard;
